import i18n from "@i18n";
import { setUserIdInSentryScope } from "@utils/sentry";
import {
  GeoLocationState,
  setGeoLocation,
} from "@utils/redux/slices/geoLocation";
import { setRouteId } from "@utils/redux/slices/routeId";
import { loadServerContext, setCookiesFromMeristemContext } from "./context";
import { getEmailAndNameFromURLParams } from "./urlParams";
import { USER_IS_BOT } from "./botDetector";

import store, { observeStore } from "./redux/store";
import { updateRecommendedPlan } from "./redux/slices/recommendedPlan";
import { updateVisitorStatusState } from "./redux/slices/visitorStatus";
import { updatePlans } from "./redux/slices/plans";
import {
  ServerContextState,
  updateServerContext,
} from "./redux/slices/serverContext";
import {
  updateUserData,
  initializeNewUser,
  fetchAndInitializeExistingUser,
} from "./redux/slices/userData";
import { updateTrialFromServerContext } from "./redux/slices/trial";
import { setLanguage } from "./redux/slices/language";
import { setDeviceInformation } from "./redux/slices/deviceInformation";
import { updateStartEvents } from "./redux/slices/startEvents";

import {
  setInitialPeopleProperties,
  updateUserContext,
  trackEvent,
  formatMeristemExperimentStateVisitData,
} from "./api/tracker";

import ConversionTracker from "./services/ConversionTracker";
import OptimizelySXExperiment from "./services/OptimizelySXExperiment";
import visitTracker from "./services/VisitTracker";
import goto from "src/pageDefinitions/goto";
import { Context } from "@noom/meristem-context";
import { routeConstants } from "./constants";
import { setInAppAutoCookie } from "./authCookieParser";
import {
  getExperimentState,
  getMeristemContext,
  getMeristemState,
  MeristemState,
} from "./meristemContext";

/**
 * Update UserData slice (and possibly fetch an existing user's UserData).
 */
async function updateUserDataSlice(
  serverContext: ServerContextState,
  meristemContext: Context
) {
  // Fetch user from backend if they are in the database.
  // Guard against bots, don't attempt to fetch userData if we detect a bot.
  if (serverContext.user_in_db !== false && !USER_IS_BOT) {
    await store.dispatch(
      fetchAndInitializeExistingUser(serverContext.user_id, meristemContext)
    );
  } else {
    await store.dispatch(initializeNewUser(serverContext, meristemContext));
  }
  const emailAndName = getEmailAndNameFromURLParams();
  if (Object.keys(emailAndName).length !== 0)
    await store.dispatch(updateUserData(emailAndName));
}

/**
 * Track changes to the user's visitor status to mixpanel
 *
 * 1. Add tracking observer to the visitorStatus slice in Redux.
 * 2. Track initial state.
 */
function trackVisitorState(serverContext: ServerContextState) {
  observeStore(
    (state) => state.visitorStatus,
    (visitorStatus) => {
      updateUserContext({
        visitorState: visitorStatus.visitorState,
      });
      trackEvent("VisitorStateDetermined", {
        ...visitorStatus,
        context_type: store.getState().serverContext.context_type,
      });
    }
  );

  // Update visitor status state on initialization to kick off VisitorStateDetermined tracking
  // Only update visitor status state on landing and payment to avoid unnecessary duplicate
  // visitorStateDetermined tracking events on post checkout contexts
  if (
    serverContext.context_type === "landing" ||
    serverContext.context_type === "payment"
  ) {
    store.dispatch(
      updateVisitorStatusState({ user_id: serverContext.user_id })
    );
  }
}

/**
 * After we fetch serverContext, Take the data and update the appropriate
 * redux slices with it.
 */
export function updateStoreWithServerContext(
  serverContext: ServerContextState
) {
  store.dispatch(updateServerContext(serverContext));
  store.dispatch(updateTrialFromServerContext(serverContext));
  if (serverContext?.plans) store.dispatch(updatePlans(serverContext.plans));
  if (serverContext?.plan)
    store.dispatch(updateRecommendedPlan(serverContext.plan));
}

/**
 * Initialize store with data from meristemContext
 * If the meristemContext window global variable is not available, the slices will fallback to default data
 */
function initializeStoreWithMeristemContextData(meristemContextData: Context) {
  store.dispatch(setDeviceInformation(meristemContextData));
  store.dispatch(setGeoLocation(meristemContextData as GeoLocationState));
  store.dispatch(setLanguage(meristemContextData.language_code));
  store.dispatch(setRouteId(meristemContextData.route_id as routeConstants));
}

/**
 * Initialize store with data from meristemState
 */
function initializeStoreWithMeristemStateData(
  meristemStateData: MeristemState
) {
  if (meristemStateData) {
    store.dispatch(updateStartEvents(meristemStateData.experiments));
  }
}

/**
 * Initialize redux, set up services, and fire off tracking calls.
 * @returns App initializes successfully
 */
export default async function initializeApplication() {
  // Expose debug tools
  (window as any).reduxState = store.getState;
  (window as any).grow = {
    setInAppAutoCookie,
    goto,
  };

  try {
    // 1 - get the meristemContext data
    const meristemContext = getMeristemContext();
    setCookiesFromMeristemContext(meristemContext);

    // 2 - get serverContext from Django API
    const serverContext = await loadServerContext();

    // 4 - update store with data from meristemContext
    initializeStoreWithMeristemContextData(meristemContext);

    // 5 - set up Sentry and attach user_id as a property
    setUserIdInSentryScope(serverContext.user_id);

    // 6 - Initialize visitTracker. This must happen before we fire any mixpanel events.
    visitTracker.init(serverContext.session_cookie_domain);

    // 7 - update the UserData slice in redux (also, fetch userData if an existing user)
    await updateUserDataSlice(serverContext, meristemContext);

    // 8 - set up mixpanel tracker
    setInitialPeopleProperties();

    // 9 - maybe send an event that signals the user is allocated to an experiment
    const experimentsState = getExperimentState() || [];
    experimentsState.forEach((experimentState) => {
      trackEvent("OnMeristemExperimentDecided", {
        experimentName: experimentState.experimentName || null,
        variationName: experimentState.variationName || null,
      });
    });

    // 10 - track a visit
    visitTracker.createOrUpdateVisit(
      formatMeristemExperimentStateVisitData(experimentsState) || {}
    );

    // 11 - set up a redux observer that updates visitor state
    trackVisitorState(serverContext);

    // 12 - set up conversion tracker for pixel tracking
    window.conversionTracker = new ConversionTracker();

    // 13 - set up a helper for SX Optimizely experiments to pass through experiment variables.
    (window as any).osxe = new OptimizelySXExperiment();

    // 15a - Update i18n language to match serverContext language
    if (i18n && meristemContext.language_code !== i18n.language) {
      i18n.changeLanguage(meristemContext.language_code);
    }
    // 15b - Set the lang property to the <html> tag.
    document.querySelector("html").lang = meristemContext.language_code;

    // 17 - Update store with data from meristemState
    initializeStoreWithMeristemStateData(getMeristemState());
    return true;
  } catch (e) {
    // Identification error page for in app errors
    goto.error(e, "initialize");
    return false;
  } finally {
    const isDevOrStaging =
      window.location.origin.includes("localhost") ||
      window.location.origin.includes("buyflow-lambda");
    const isExcludingQuicksilver = window.location.search.includes(
      "excludeQuicksilver=true"
    );
    if (isDevOrStaging && !isExcludingQuicksilver) {
      import(
        /* webpackChunkName: "quicksilver" */ "./qa/initializeQuicksilver"
      ).then((module) => module && module.default());
    }
  }
}
